import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Trash2, 
  Loader2,
  Building,
  Tag,
  Users,
  Target,
  Settings as SettingsIcon,
  PoundSterling,
  DollarSign,
  Euro,
  Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const Settings = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [metricsLibrary, setMetricsLibrary] = useState([]);
  const [systemSettings, setSystemSettings] = useState({ currency_symbol: '£', currency_code: 'GBP' });
  
  // Dialog states
  const [showDeptDialog, setShowDeptDialog] = useState(false);
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [showMetricDialog, setShowMetricDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptApprover, setNewDeptApprover] = useState('');
  const [newCatName, setNewCatName] = useState('');
  const [newCatType, setNewCatType] = useState('process_area');
  
  // New metric form
  const [newMetric, setNewMetric] = useState({
    name: '',
    category: 'Efficiency',
    unit: '',
    description: ''
  });

  // User dialog (CI Manager scope)
  const [userDialog, setUserDialog] = useState({ open: false, mode: 'create', data: null });
  const [userForm, setUserForm] = useState({
    email: '', name: '', password: '', role: 'Submitter', department: '',
  });
  
  const isAdminUser = isAdmin();

  useEffect(() => {
    if (isAdminUser) {
      fetchData();
    }
  }, [isAdminUser]);

  // Redirect non-admins
  if (!isAdminUser) {
    return <Navigate to="/dashboard" replace />;
  }

  const fetchData = async () => {
    try {
      const [deptRes, catRes, usersRes, approversRes, metricsRes, settingsRes, procRes] = await Promise.all([
        api.get('/settings/departments'),
        api.get('/settings/categories'),
        api.get('/company/users'),
        api.get('/users/approvers'),
        api.get('/metrics-library'),
        api.get('/settings'),
        api.get('/processes?level=2'),
      ]);
      
      // Use L2 processes as departments if available, fallback to settings
      setDepartments(procRes.data.length > 0 ? procRes.data.map(p => ({ id: p.id, name: p.name, process_owner: p.process_owner })) : deptRes.data);
      setCategories(catRes.data);
      setUsers(usersRes.data);
      setApprovers(approversRes.data);
      setMetricsLibrary(metricsRes.data);
      setSystemSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch settings data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ===== User management =====
  const openCreateUser = () => {
    setUserForm({ email: '', name: '', password: '', role: 'Submitter', department: '' });
    setUserDialog({ open: true, mode: 'create', data: null });
  };
  const openEditUser = (u) => {
    setUserForm({
      email: u.email, name: u.name, password: '',
      role: u.role, department: u.department || '',
    });
    setUserDialog({ open: true, mode: 'edit', data: u });
  };
  const saveUser = async () => {
    setSubmitting(true);
    try {
      if (userDialog.mode === 'create') {
        if (!userForm.email || !userForm.name || !userForm.password) {
          toast.error('Email, name and password are required');
          setSubmitting(false);
          return;
        }
        await api.post('/company/users', userForm);
        toast.success('User added');
      } else {
        const payload = { ...userForm };
        delete payload.email;
        if (!payload.password) delete payload.password;
        await api.put(`/company/users/${userDialog.data.id}`, payload);
        toast.success('User updated');
      }
      setUserDialog({ open: false, mode: 'create', data: null });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save user');
    } finally {
      setSubmitting(false);
    }
  };
  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.name} from this company?`)) return;
    try {
      await api.delete(`/company/users/${u.id}`);
      toast.success('User removed');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove user');
    }
  };

  const handleAddDepartment = async () => {
    if (!newDeptName) {
      toast.error('Department name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/settings/departments', {
        name: newDeptName,
        default_approver_id: newDeptApprover || null
      });
      toast.success('Department added');
      setShowDeptDialog(false);
      setNewDeptName('');
      setNewDeptApprover('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add department');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCatName) {
      toast.error('Category name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/settings/categories', {
        name: newCatName,
        type: newCatType
      });
      toast.success('Category added');
      setShowCatDialog(false);
      setNewCatName('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMetric = async () => {
    if (!newMetric.name || !newMetric.unit) {
      toast.error('Metric name and unit are required');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/metrics-library', {
        ...newMetric,
        is_active: true
      });
      toast.success('Metric added to library');
      setShowMetricDialog(false);
      setNewMetric({ name: '', category: 'Efficiency', unit: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add metric');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMetric = async (metricId) => {
    if (!confirm('Are you sure you want to remove this metric?')) return;
    
    try {
      await api.delete(`/metrics-library/${metricId}`);
      toast.success('Metric removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove metric');
    }
  };

  const handleUpdateCurrency = async (symbol, code) => {
    try {
      await api.put('/settings', {
        ...systemSettings,
        currency_symbol: symbol,
        currency_code: code
      });
      setSystemSettings({ ...systemSettings, currency_symbol: symbol, currency_code: code });
      toast.success('Currency updated');
    } catch (error) {
      toast.error('Failed to update currency');
    }
  };

  const getApproverName = (approverId) => {
    const approver = users.find(u => u.id === approverId);
    return approver?.name || 'Not assigned';
  };

  const processAreas = categories.filter(c => c.type === 'process_area');
  const locations = categories.filter(c => c.type === 'location');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#235894]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Settings</h1>
        <p className="text-slate-500 mt-1">Manage departments, categories, and system configuration</p>
      </div>

      <Tabs defaultValue="departments">
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="departments" className="flex items-center gap-2">
            <Building className="w-4 h-4" />
            <span className="hidden sm:inline">Departments</span>
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <Tag className="w-4 h-4" />
            <span className="hidden sm:inline">Categories</span>
          </TabsTrigger>
          <TabsTrigger value="metrics" className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            <span className="hidden sm:inline">Metrics</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Users</span>
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center gap-2">
            <SettingsIcon className="w-4 h-4" />
            <span className="hidden sm:inline">System</span>
          </TabsTrigger>
        </TabsList>

        {/* Departments Tab */}
        <TabsContent value="departments" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Departments (Value Streams)</CardTitle>
                <CardDescription>Departments are managed via the Process Hierarchy (Level 2). Go to Processes to add or edit.</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Department Name</TableHead>
                    <TableHead>Process Owner</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {departments.map((dept) => (
                    <TableRow key={dept.id}>
                      <TableCell className="font-medium">{dept.name}</TableCell>
                      <TableCell>{dept.process_owner || dept.default_approver_id ? getApproverName(dept.default_approver_id) : '-'}</TableCell>
                    </TableRow>
                  ))}
                  {departments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center text-slate-400 py-8">
                        No departments configured. Add L2 processes in the Process Hierarchy.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="mt-6 space-y-6">
          {/* Process Areas */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Process Areas</CardTitle>
                <CardDescription>Business process categories for ideas</CardDescription>
              </div>
              <Button onClick={() => { setNewCatType('process_area'); setShowCatDialog(true); }} data-testid="add-process-area-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Process Area
              </Button>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {processAreas.map((cat) => (
                  <div 
                    key={cat.id}
                    className="px-3 py-1.5 bg-slate-100 rounded-full text-sm text-slate-700"
                  >
                    {cat.name}
                  </div>
                ))}
                {processAreas.length === 0 && (
                  <p className="text-slate-400">No process areas configured</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Locations */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Locations / Regions</CardTitle>
                <CardDescription>Geographic locations for ideas and projects</CardDescription>
              </div>
              <Button onClick={() => { setNewCatType('location'); setShowCatDialog(true); }} data-testid="add-location-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Location
              </Button>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {locations.map((cat) => (
                  <div 
                    key={cat.id}
                    className="px-3 py-1.5 bg-slate-100 rounded-full text-sm text-slate-700"
                  >
                    {cat.name}
                  </div>
                ))}
                {locations.length === 0 && (
                  <p className="text-slate-400">No locations configured</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Users</CardTitle>
                <CardDescription>Manage users within this company</CardDescription>
              </div>
              <Button onClick={openCreateUser} className="bg-[#235894] hover:bg-[#1E4B80]" data-testid="add-company-user-btn">
                <Plus className="w-4 h-4 mr-2" /> Add user
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          user.role === 'CI Manager' ? 'bg-purple-100 text-purple-700' :
                          user.role === 'Approver' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {user.role}
                        </span>
                      </TableCell>
                      <TableCell>{user.department || '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEditUser(user)} data-testid={`edit-user-${user.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => removeUser(user)} data-testid={`remove-user-${user.id}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {users.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No users yet — click "Add user" to invite someone.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Metrics Library Tab */}
        <TabsContent value="metrics" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Operational Metrics Library</CardTitle>
                <CardDescription>Define standard metrics for tracking non-financial performance</CardDescription>
              </div>
              <Button onClick={() => setShowMetricDialog(true)} data-testid="add-metric-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Metric
              </Button>
            </CardHeader>
            <CardContent>
              {['Efficiency', 'Quality', 'Service & Experience', 'People & Risk'].map((category) => {
                const categoryMetrics = metricsLibrary.filter(m => m.category === category);
                return (
                  <div key={category} className="mb-6 last:mb-0">
                    <h4 className={cn(
                      "text-sm font-semibold mb-3 px-2 py-1 rounded-md inline-block",
                      category === 'Efficiency' && "bg-blue-100 text-blue-800",
                      category === 'Quality' && "bg-purple-100 text-purple-800",
                      category === 'Service & Experience' && "bg-amber-100 text-amber-800",
                      category === 'People & Risk' && "bg-red-100 text-red-800"
                    )}>
                      {category}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {categoryMetrics.map((metric) => (
                        <div 
                          key={metric.id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg group"
                        >
                          <div>
                            <p className="font-medium text-slate-900">{metric.name}</p>
                            <p className="text-xs text-slate-500">Unit: {metric.unit}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteMetric(metric.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {categoryMetrics.length === 0 && (
                        <p className="text-slate-400 text-sm col-span-full">No metrics in this category</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Settings Tab */}
        <TabsContent value="system" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Currency</CardTitle>
                <CardDescription>Set the default currency for financial tracking</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  <Button
                    variant={systemSettings.currency_symbol === '£' ? 'default' : 'outline'}
                    className={systemSettings.currency_symbol === '£' ? 'bg-[#235894]' : ''}
                    onClick={() => handleUpdateCurrency('£', 'GBP')}
                  >
                    <PoundSterling className="w-4 h-4 mr-2" />
                    GBP (£)
                  </Button>
                  <Button
                    variant={systemSettings.currency_symbol === '$' ? 'default' : 'outline'}
                    className={systemSettings.currency_symbol === '$' ? 'bg-[#235894]' : ''}
                    onClick={() => handleUpdateCurrency('$', 'USD')}
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    USD ($)
                  </Button>
                  <Button
                    variant={systemSettings.currency_symbol === '€' ? 'default' : 'outline'}
                    className={systemSettings.currency_symbol === '€' ? 'bg-[#235894]' : ''}
                    onClick={() => handleUpdateCurrency('€', 'EUR')}
                  >
                    <Euro className="w-4 h-4 mr-2" />
                    EUR (€)
                  </Button>
                </div>
                <p className="text-sm text-slate-500 mt-4">
                  Current: <strong>{systemSettings.currency_code} ({systemSettings.currency_symbol})</strong>
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>System Information</CardTitle>
                <CardDescription>Application statistics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Total Users</span>
                  <span className="font-medium">{users.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Departments</span>
                  <span className="font-medium">{departments.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Metrics in Library</span>
                  <span className="font-medium">{metricsLibrary.length}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Metric Dialog */}
      <Dialog open={showMetricDialog} onOpenChange={setShowMetricDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Operational Metric</DialogTitle>
            <DialogDescription>
              Add a new metric to the standard library
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Metric Name *</Label>
              <Input
                value={newMetric.name}
                onChange={(e) => setNewMetric({ ...newMetric, name: e.target.value })}
                placeholder="e.g., Cycle Time"
                data-testid="new-metric-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select 
                  value={newMetric.category} 
                  onValueChange={(v) => setNewMetric({ ...newMetric, category: v })}
                >
                  <SelectTrigger data-testid="new-metric-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Efficiency">Efficiency</SelectItem>
                    <SelectItem value="Quality">Quality</SelectItem>
                    <SelectItem value="Service & Experience">Service & Experience</SelectItem>
                    <SelectItem value="People & Risk">People & Risk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Unit *</Label>
                <Input
                  value={newMetric.unit}
                  onChange={(e) => setNewMetric({ ...newMetric, unit: e.target.value })}
                  placeholder="e.g., minutes, %, count"
                  data-testid="new-metric-unit-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={newMetric.description}
                onChange={(e) => setNewMetric({ ...newMetric, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMetricDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddMetric} disabled={submitting} className="bg-[#235894]">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Metric
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Department Dialog */}
      <Dialog open={showDeptDialog} onOpenChange={setShowDeptDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Department</DialogTitle>
            <DialogDescription>
              Create a new department and optionally assign a default approver
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Department Name *</Label>
              <Input
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
                placeholder="e.g., Operations"
                data-testid="new-dept-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Default Approver</Label>
              <Select value={newDeptApprover} onValueChange={setNewDeptApprover}>
                <SelectTrigger data-testid="new-dept-approver-select">
                  <SelectValue placeholder="Select approver" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {approvers.map((approver) => (
                    <SelectItem key={approver.id} value={approver.id}>
                      {approver.name} ({approver.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeptDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddDepartment}
              disabled={submitting || !newDeptName}
              className="bg-[#235894] hover:bg-[#1E4B80]"
              data-testid="confirm-add-dept-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Department
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={showCatDialog} onOpenChange={setShowCatDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Add {newCatType === 'process_area' ? 'Process Area' : 'Location'}
            </DialogTitle>
            <DialogDescription>
              Create a new {newCatType === 'process_area' ? 'process area category' : 'location/region'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                placeholder={newCatType === 'process_area' ? 'e.g., Invoice Processing' : 'e.g., London'}
                data-testid="new-cat-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCatDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddCategory}
              disabled={submitting || !newCatName}
              className="bg-[#235894] hover:bg-[#1E4B80]"
              data-testid="confirm-add-cat-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add {newCatType === 'process_area' ? 'Process Area' : 'Location'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Dialog (Add / Edit) */}
      <Dialog open={userDialog.open} onOpenChange={(open) => setUserDialog({ ...userDialog, open })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{userDialog.mode === 'create' ? 'Add user' : 'Edit user'}</DialogTitle>
            <DialogDescription>
              {userDialog.mode === 'create'
                ? 'Create a user inside this company. They will be able to log in immediately.'
                : 'Update the user’s name, role, department, or password.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} data-testid="user-form-name" />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={userForm.email}
                  disabled={userDialog.mode === 'edit'}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  data-testid="user-form-email"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{userDialog.mode === 'edit' ? 'New password (leave blank to keep current)' : 'Password *'}</Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                data-testid="user-form-password"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                  <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Submitter">Submitter</SelectItem>
                    <SelectItem value="Approver">Approver</SelectItem>
                    <SelectItem value="CI Manager">CI Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Input value={userForm.department} onChange={(e) => setUserForm({ ...userForm, department: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialog({ ...userDialog, open: false })}>Cancel</Button>
            <Button onClick={saveUser} disabled={submitting} className="bg-[#235894] hover:bg-[#1E4B80]" data-testid="save-user-btn">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {userDialog.mode === 'create' ? 'Add user' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
